import { yupResolver } from "@hookform/resolvers/yup";
import CircularProgress from "@mui/material/CircularProgress/CircularProgress";
import { FC, useEffect, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import * as Yup from "yup";
import { setSmsPhoneNumber, setTransactionId, useAppDispatch } from "../../../../logic/src/redux";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { RadioInput } from "../Form";
import { RadioGroup } from "../Form/RadioGroup";
import { DefaultStepProps } from "../Stepper/CustomStepper";
import * as Styled from "./TelephoneNumberSelectionPage.styled";

type FormValues = {
    telephoneNumberPreference: string;
};

export const TelephoneNumberSelectionPage: FC<DefaultStepProps> = ({
    queries: { phoneNumbersQuery, sendSMSVerificationCodeMutation },
    setContinueCondition,
}) => {
    const { t } = useTypedTranslation();
    const schema = useMemo(
        () =>
            Yup.object({
                telephoneNumberPreference: Yup.string()
                    .trim()
                    .min(7, t("investmentStrategy.alerts.phoneNumberSelection.tooShortError"))
                    .defined(),
            }),
        [t],
    );
    const methods = useForm<FormValues>({
        defaultValues: { telephoneNumberPreference: "" },
        resolver: yupResolver(schema),
        mode: "onChange",
    });
    const dispatch = useAppDispatch();

    const {
        data: phoneNumbers,
        refetch: refetchPhoneNumbers,
        isLoading: isLoadingPhoneNumbers,
    } = phoneNumbersQuery ?? {};
    // fetch precontractualDocs if not available
    useEffect(() => {
        if (!phoneNumbers) refetchPhoneNumbers?.();
    }, [refetchPhoneNumbers, phoneNumbers]);

    // watch selected value
    const selected = useWatch({ control: methods.control, name: "telephoneNumberPreference" });

    // Register continue condition so step cannot advance without selection
    useEffect(() => {
        if (!setContinueCondition) return;
        setContinueCondition(async () => {
            const isValid = await methods.trigger();
            if (!isValid) return false;
            const value = methods.getValues("telephoneNumberPreference");
            dispatch(setSmsPhoneNumber(value)); //Save selected phone number to state
            try {
                const response = await sendSMSVerificationCodeMutation?.mutateAsync({ phoneNumber: value });
                dispatch(setTransactionId(response?.transactionId ?? "")); // Save transactionId to state for final verification step
            } catch {
                return false;
            }
            return true;
        });
    }, [setContinueCondition, selected, methods, dispatch, sendSMSVerificationCodeMutation]);

    return (
        <FormProvider {...methods}>
            <form>
                <Styled.Wrapper>
                    <Styled.Title>{t("investmentStrategy.phoneNumberSelection.title")}</Styled.Title>
                    <Styled.Paragraph>{t("investmentStrategy.phoneNumberSelection.description")}</Styled.Paragraph>
                    {isLoadingPhoneNumbers ? (
                        <Styled.LoadingWrapper>
                            <CircularProgress />
                        </Styled.LoadingWrapper>
                    ) : (
                        <Styled.RadioGroupWrapper>
                            <RadioGroup name="telephoneNumberPreference">
                                {phoneNumbers?.map((number) => (
                                    <RadioInput
                                        id={number}
                                        name={"telephoneNumberPreference"}
                                        label={number}
                                        value={number}
                                        key={number}
                                    />
                                ))}
                            </RadioGroup>
                        </Styled.RadioGroupWrapper>
                    )}
                </Styled.Wrapper>
            </form>
        </FormProvider>
    );
};
